import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import { useTranslation } from "react-i18next";
import {
  useAccountManagementTasks,
  useAccountManagementComments,
  useTriggerAccountManagementExecution,
} from "../../../hooks/useAccountManagement";
import AccountManagementTaskTable from "./AccountManagementTaskTable";
import AccountManagementCommentSection from "./AccountManagementCommentSection";

interface AccountManagementTabProps {
  projectId: number;
  isLead: boolean;
  currentUserId: number;
  clientUsers: { id: number; firstName: string; lastName: string }[];
  freelancerUser?: { id: number; firstName: string; lastName: string };
}

const AccountManagementTab: React.FC<AccountManagementTabProps> = ({
  projectId,
  isLead,
  currentUserId,
  clientUsers,
  freelancerUser,
}) => {
  const { t } = useTranslation();
  const {
    data: tasks,
    isLoading: tasksLoading,
    error: tasksError,
  } = useAccountManagementTasks(projectId);
  const {
    data: comments,
    isLoading: commentsLoading,
    error: commentsError,
  } = useAccountManagementComments(projectId);

  const triggerExecution = useTriggerAccountManagementExecution();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirmRun = () => {
    setConfirmOpen(false);
    triggerExecution.mutate({ projectId });
  };

  if (tasksLoading || commentsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tasksError || commentsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t("accountManagement.loadError", "Erreur lors du chargement des donn\u00e9es")}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "text.secondary",
            letterSpacing: "0.2px",
          }}
        >
          {t("accountManagement.trackingTitle", "Suivi de l'Account Management")}
        </Typography>

        {isLead && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<BoltIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={triggerExecution.isPending}
          >
            {t(
              "accountManagement.runDueTasks",
              "Exécuter les tâches à échéance"
            )}
          </Button>
        )}
      </Box>

      <AccountManagementTaskTable
        tasks={tasks || []}
        comments={comments || []}
        projectId={projectId}
        isLead={isLead}
      />

      <AccountManagementCommentSection
        projectId={projectId}
        comments={comments || []}
        tasks={tasks || []}
        isLead={isLead}
        currentUserId={currentUserId}
        clientUsers={clientUsers}
        freelancerUser={freelancerUser}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t(
            "accountManagement.runDueTasksConfirmTitle",
            "Exécuter les tâches à échéance ?"
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "accountManagement.runDueTasksConfirmBody",
              "Les tâches planifiées de ce projet dont la date est atteinte vont être traitées et leurs e-mails envoyés. Continuer ?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t("common.cancel", "Annuler")}
          </Button>
          <Button onClick={handleConfirmRun} variant="contained" autoFocus>
            {t("accountManagement.runDueTasksConfirm", "Exécuter")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountManagementTab;
